import React, {
  useReducer,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { produce } from "immer";
import styled from "styled-components";
import "styled-components/macro";

import Modal from "components/Modal";
import Sidekick from "components/Sidekick";
import Btn from "components/Btn";
import Table from "components/Table";
import { toastContext } from "components/Toast/Toast";

import { userSelector } from "store/user";
import {
  togglePermissionsWindow,
  permissionsWindowMetaSelector,
  fetchPermissions,
  permissionsSelector,
} from "store/permissions";
import { setPermissions } from "store/genericItem";

/*
id
nome
permission: 
    - 0 = niente;
    - 1 = lettura;
    - 2 = lettura e scrittura;
*/

function reducer(users = [], action) {
  const { type, payload } = action;
  if (type === "SET_PERMESSO") {
    const { id, toggle, value } = payload;

    return produce(users, draft => {
      const user = draft.find(item => item.id === id);
      if (user) {
        user.permission = toggle ? value : value - 1;
      }
    });
  }
  if (type === "SET_PERMESSO_TUTTI") {
    const { toggle, value } = payload;
    return produce(users, draft => {
      draft.forEach(
        (user, i) => (user.permission = toggle ? value : value - 1)
      );
    });
  }
  return users;
}

function setPermesso(id, toggle, value) {
  return {
    type: "SET_PERMESSO",
    payload: {
      id,
      toggle,
      value,
    },
  };
}

function setPermessiTutti(toggle, value) {
  return {
    type: "SET_PERMESSO_TUTTI",
    payload: {
      toggle,
      value,
    },
  };
}

export function Permissions(props) {
  const { onConfirm, onCancel, submitted, showApplyToChildren } = props;
  const [permissions, dispatch] = useReducer(reducer, props.permissions);
  const [applyToChildren, setApplyToChildren] = useState(false);

  const onCheckboxClick = (id, e) => {
    dispatch(setPermesso(id, e.target.checked, parseInt(e.target.value, 10)));
  };

  const onCheckboxTuttiClick = e => {
    dispatch(setPermessiTutti(e.target.checked, parseInt(e.target.value, 10)));
  };

  const tuttiLettura = useMemo(() => {
    const filteredUsers =
      permissions && permissions.filter(user => user.permission >= 1);

    return filteredUsers && filteredUsers.length === permissions.length;
  }, [permissions]);

  const tuttiScrittura = useMemo(() => {
    const filteredUsers =
      permissions && permissions.filter(user => user.permission === 2);

    return filteredUsers && filteredUsers.length === permissions.length;
  }, [permissions]);

  return (
    <>
      <Sidekick.Title>Gestione permessi</Sidekick.Title>
      <Table smallPadding>
        <thead>
          <tr>
            <td width="60%">Nome</td>
            <td width="20%">Lettura</td>
            <td width="20%">Scrittura</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Sidekick.SmallTitle noMargin>Tutti</Sidekick.SmallTitle>
            </td>
            <td>
              <input
                type="checkbox"
                value={1}
                onChange={onCheckboxTuttiClick}
                checked={tuttiLettura}
              />
            </td>
            <td>
              <input
                type="checkbox"
                value={2}
                onChange={onCheckboxTuttiClick}
                checked={tuttiScrittura}
              />
            </td>
          </tr>
          {permissions &&
            permissions.map(user => (
              <tr key={user.id}>
                <td>
                  <Sidekick.Label noPointer noMarginBottom size="big">
                    {user.nome}

                    {user.isAuthor && (
                      <Sidekick.Label noPointer noMarginBottom size="small">
                        Author
                      </Sidekick.Label>
                    )}
                  </Sidekick.Label>
                </td>
                <td>
                  <input
                    type="checkbox"
                    value={1}
                    onChange={e => onCheckboxClick(user.id, e)}
                    checked={user.permission >= 1}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    value={2}
                    onChange={e => onCheckboxClick(user.id, e)}
                    checked={user.permission >= 2}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {showApplyToChildren && (
        <MarkerDataWrapper>
          <Sidekick.Label as="label" size="big">
            <input
              type="checkbox"
              value={0}
              onChange={() => setApplyToChildren(s => !s)}
              checked={applyToChildren}
            />
            Applica questi permessi a tutti i figli
          </Sidekick.Label>
        </MarkerDataWrapper>
      )}
      <ButtonsWrapper>
        <Btn onClick={onCancel}>Annulla</Btn>
        <Btn
          highlighted
          disabled={submitted}
          onClick={() => onConfirm(permissions, applyToChildren)}
        >
          {submitted ? "Attendere" : "Conferma"}
        </Btn>
      </ButtonsWrapper>
    </>
  );
}

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  margin-top: 1rem;
`;

const MarkerDataWrapper = styled.div`
  margin: 1rem 0;
  padding: 1rem 0;

  input {
    margin-right: 7px;
  }
`;

export default function PermissionsWithData(props) {
  const { sendToast } = React.useContext(toastContext);

  const userId = useSelector(userSelector);
  const permissions = useSelector(permissionsSelector);

  const { mounted, loading, submitted, id: itemId, itemType } = useSelector(
    permissionsWindowMetaSelector
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (mounted) {
      dispatch(fetchPermissions({ id: itemId, itemType }));
    }
  }, [mounted, dispatch]);

  const onConfirm = useCallback(
    async (newPermissions, applyToChildren) => {
      try {
        await dispatch(
          setPermissions({
            id: itemId,
            itemType,
            permissions,
            newPermissions,
            applyToChildren: applyToChildren ? 1 : 0,
          })
        );

        dispatch(togglePermissionsWindow());
        sendToast({ type: "SUCCESS", message: `Permessi salvati` });
      } catch (err) {
        dispatch(togglePermissionsWindow());
        sendToast({
          type: "ERROR",
          message: `Errore durante il salvataggio`,
        });
      }
    },
    [itemId, itemType, permissions, dispatch]
  );

  const onCancel = useCallback(() => {
    dispatch(togglePermissionsWindow());
  }, [itemId, dispatch]);

  if (!mounted || itemId === 0) return null;

  // Mostrare il checkbox per applicare i permessi a tutti i figli solo se
  // itemType è progetto, rilievo o marker
  const showApplyToChildren = [0, 1, 2].includes(itemType);

  return (
    <Modal onCloseClick={() => dispatch(togglePermissionsWindow())}>
      {!loading && permissions && permissions.length > 0 ? (
        <Permissions
          permissions={permissions}
          userId={userId}
          onConfirm={onConfirm}
          onCancel={onCancel}
          submitted={submitted}
          showApplyToChildren={showApplyToChildren}
        />
      ) : (
        <Sidekick.Loading />
      )}
    </Modal>
  );
}
